import React, { useCallback, useContext, useEffect, useState } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";

import { PostExpert } from "../../Widgets/Log/log.typing";
import { SlackSearchResultsContext } from "../Context/SlackSearchResultsContext";
import { MachineQrCodePreview } from "./MachineQrCodePreview";
import { useMachineCreationConfirmation } from "./useMachineCreationConfirmation";
import {
  createMachine,
  editMachine,
  fetchMachineConfiguration,
  fetchMachineGroups,
  fetchMachineShopUsers,
  readApiError,
} from "./api/machineEditorApi";
import {
  MACHINE_CODE_HINT,
  MACHINE_CODE_MAX_LENGTH,
  MACHINE_NAME_MAX_LENGTH,
  sanitizeMachineCode,
} from "./machineCodeRules";
import {
  MachineConfiguration,
  MachineConfigurationChange,
  MachineEditorMode,
  MachineGroupOption,
  MachineQrCode,
} from "./machineEditor.typing";

interface MachineEditorDialogProps {
  open: boolean;
  mode: MachineEditorMode;
  /** Required in edit mode - the machine whose configuration is being changed. */
  machineId?: string;
  onClose: () => void;
  /** Fired after a successful save, so the caller can refresh what it is showing. */
  onSaved?: (machine: MachineConfiguration) => void;
}

const SURFACE = "#383838";
const MUTED = "#c9b4ca";

const surface = { backgroundColor: SURFACE, color: "#fff" };

/**
 * MUI's defaults come from the light theme, so labels, helper text and the switch label all land as
 * dark grey on this dark surface. Every one of them is restated here.
 */
const fieldStyle = {
  "& .MuiInputBase-input": { color: "#fff" },
  "& .MuiInputBase-input::placeholder": { color: MUTED, opacity: 1 },
  "& .MuiInputLabel-root": { color: MUTED },
  "& .MuiInputLabel-root.Mui-focused": { color: "#d7a8d9" },
  "& .MuiFormHelperText-root": { color: MUTED },
  "& .MuiFormHelperText-root.Mui-error": { color: "#ff8f8f" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#6d566e" },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#a665a8" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#a665a8",
  },
  "& .MuiSvgIcon-root": { color: MUTED },
};

/** The suggestion list sits below the field rather than over it, so what was typed stays readable. */
const autocompleteSlotProps = {
  paper: {
    sx: {
      backgroundColor: "#222529",
      color: "#fff",
      "& .MuiAutocomplete-option:hover": { backgroundColor: "#3a3f46" },
      '& .MuiAutocomplete-option[aria-selected="true"]': {
        backgroundColor: "#4a3350",
      },
      "& .MuiAutocomplete-noOptions": { color: MUTED },
    },
  },
  popper: { placement: "bottom-start" as const },
};

const rowStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: "18px",
  // Fills whatever the dialog gives it. A fixed min-width overflowed a 375px phone and cut the
  // fields and the availability hint off at the right edge.
  width: "100%",
  minWidth: 0,
  paddingTop: "8px",
};

const changeListStyle: React.CSSProperties = {
  margin: 0,
  paddingLeft: "18px",
  fontSize: "13px",
  lineHeight: 1.7,
};

const hintStyle: React.CSSProperties = {
  fontSize: "12px",
  color: MUTED,
  margin: "-10px 0 0 14px",
  paddingRight: "14px",
  lineHeight: 1.5,
};

const describeChange = (change: MachineConfigurationChange) =>
  `${change.Field}: ${change.OldValue ?? "none"} → ${change.NewValue ?? "none"}`;

export const MachineEditorDialog: React.FC<MachineEditorDialogProps> = ({
  open,
  mode,
  machineId,
  onClose,
  onSaved,
}) => {
  const searchResultsContext = useContext(SlackSearchResultsContext);

  const [groups, setGroups] = useState<MachineGroupOption[]>([]);
  const [users, setUsers] = useState<PostExpert[]>([]);

  const [machineName, setMachineName] = useState("");
  const [machineCode, setMachineCode] = useState("");
  const [group, setGroup] = useState<MachineGroupOption | null>(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const [editor, setEditor] = useState<PostExpert | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [savedMachine, setSavedMachine] = useState<MachineConfiguration | null>(null);
  const [qrCode, setQrCode] = useState<MachineQrCode | null>(null);
  const [changes, setChanges] = useState<MachineConfigurationChange[] | null>(null);

  // The hub connection is opened as soon as the popover opens, so the creation signal cannot arrive
  // before we are listening for it.
  const confirmation = useMachineCreationConfirmation(open && mode === "create");

  const resetForm = useCallback(() => {
    setMachineName("");
    setMachineCode("");
    setGroup(null);
    setIsAvailable(true);
    setEditor(null);
    setError(null);
    setSavedMachine(null);
    setQrCode(null);
    setChanges(null);
    setIsSaving(false);
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    resetForm();
    confirmation.reset();

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      try {
        const [loadedGroups, loadedUsers] = await Promise.all([
          fetchMachineGroups(),
          fetchMachineShopUsers(),
        ]);
        if (cancelled) return;

        setGroups(loadedGroups);
        setUsers(loadedUsers);

        if (mode === "edit" && machineId) {
          const configuration = await fetchMachineConfiguration(machineId);
          if (cancelled) return;

          setMachineName(configuration.MachineName ?? "");
          setMachineCode(sanitizeMachineCode(configuration.MachineCode ?? ""));
          setGroup(
            loadedGroups.find((option) => option.Id === configuration.GroupId) ?? null
          );
          setIsAvailable(configuration.IsAvailable);
        }
      } catch (e) {
        if (!cancelled) {
          setError(readApiError(e, "Could not load the machine."));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
    // resetForm and confirmation are stable enough for this to run once per opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, machineId]);

  const close = useCallback(() => {
    // Whether the user saved or backed out, the machine list behind the popover may be stale.
    searchResultsContext?.fetchMachineLogs();
    onClose();
  }, [onClose, searchResultsContext]);

  const save = useCallback(async () => {
    setError(null);

    // Mirrors what MachineConfigurationService enforces, so an obvious mistake does not cost a
    // round trip. The server still has the final say.
    if (machineCode.trim().length === 0) {
      setError("A machine code is required.");
      return;
    }

    if (machineName.trim().length === 0) {
      setError("A machine name is required.");
      return;
    }

    if (!group) {
      setError("Select the group this machine belongs to.");
      return;
    }

    if (!editor) {
      setError("Select the user making this change.");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        MachineName: machineName,
        MachineCode: machineCode,
        GroupId: group.Id,
        IsAvailable: isAvailable,
        EditorId: editor.Id,
      };

      const result =
        mode === "create"
          ? await createMachine(payload)
          : await editMachine({ ...payload, MachineId: machineId as string });

      setSavedMachine(result.Machine);
      setChanges(result.Changes ?? []);
      onSaved?.(result.Machine);

      // The new machine's log post exists now, so refresh the list behind the popover rather than
      // making the user wait until it closes.
      searchResultsContext?.fetchMachineLogs();

      if (mode === "create" && result.QrCode) {
        setQrCode(result.QrCode);
        // Nothing is printable until this resolves - it is the same read a scanner performs.
        await confirmation.confirm(result.Machine.MachineId);
      }
    } catch (e) {
      setError(
        readApiError(
          e,
          mode === "create"
            ? "The machine could not be created."
            : "The machine could not be updated."
        )
      );
    } finally {
      setIsSaving(false);
    }
  }, [
    confirmation,
    editor,
    group,
    isAvailable,
    machineCode,
    machineId,
    machineName,
    mode,
    onSaved,
    searchResultsContext,
  ]);

  const retryConfirmation = useCallback(() => {
    if (savedMachine) {
      confirmation.confirm(savedMachine.MachineId);
    }
  }, [confirmation, savedMachine]);

  const isShowingQrCode = Boolean(savedMachine && qrCode);
  const isShowingEditSummary = Boolean(savedMachine && mode === "edit");

  const title = (() => {
    if (isShowingQrCode) return "Machine QR code";
    if (isShowingEditSummary) return "Machine updated";
    return mode === "create" ? "New machine" : "Edit machine";
  })();

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="xs"
      PaperProps={{ style: surface }}
    >
      <DialogTitle style={surface}>{title}</DialogTitle>

      <DialogContent style={surface}>
        {error && (
          <div style={{ color: "#ff8f8f", paddingBottom: "12px" }}>
            {error}
          </div>
        )}

        {isShowingQrCode && savedMachine && qrCode && (
          <MachineQrCodePreview
            machine={savedMachine}
            qrCode={qrCode}
            stage={confirmation.stage}
            signalReceived={confirmation.signalReceived}
            attempts={confirmation.attempts}
            onRetry={retryConfirmation}
            showEmailNotice={mode === "create"}
          />
        )}

        {isShowingEditSummary && savedMachine && (
          <div style={{ minWidth: 0 }}>
            <strong>
              [{savedMachine.MachineCode}] {savedMachine.MachineName}
            </strong>
            {changes && changes.length > 0 ? (
              <ul style={changeListStyle}>
                {changes.map((change) => (
                  <li key={change.Field}>{describeChange(change)}</li>
                ))}
              </ul>
            ) : (
              <div style={{ fontSize: "13px", paddingTop: "8px" }}>
                Nothing changed, so no log post was written.
              </div>
            )}
            <div style={{ fontSize: "12px", color: MUTED, paddingTop: "12px" }}>
              The QR code is unchanged - it encodes the machine id, which an edit never
              touches.
            </div>
          </div>
        )}

        {!savedMachine &&
          (isLoading ? (
            <div style={{ padding: "32px", textAlign: "center" }}>
              <CircularProgress size={28} />
            </div>
          ) : (
            <div style={rowStyle}>
              <TextField
                label="Machine code"
                value={machineCode}
                // Anything outside A-Z and 0-9 is dropped as it is typed, lower case is folded up
                // and the value is capped, so the field always shows what will be stored.
                onChange={(e) => setMachineCode(sanitizeMachineCode(e.target.value))}
                helperText={MACHINE_CODE_HINT}
                sx={fieldStyle}
                autoFocus={mode === "create"}
                inputProps={{
                  spellCheck: false,
                  autoComplete: "off",
                  autoCapitalize: "characters",
                  maxLength: MACHINE_CODE_MAX_LENGTH,
                  style: { textTransform: "uppercase" },
                }}
              />

              <TextField
                label="Machine name"
                value={machineName}
                onChange={(e) => setMachineName(e.target.value)}
                sx={fieldStyle}
                inputProps={{
                  spellCheck: false,
                  autoComplete: "off",
                  maxLength: MACHINE_NAME_MAX_LENGTH,
                }}
              />

              <Autocomplete
                options={groups}
                value={group}
                onChange={(_, selected) => setGroup(selected)}
                getOptionLabel={(option) => option.GroupName ?? ""}
                isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
                autoHighlight
                openOnFocus
                slotProps={autocompleteSlotProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Group"
                    placeholder="Start typing to search groups"
                    InputLabelProps={{ shrink: true }}
                    sx={fieldStyle}
                  />
                )}
              />

              <div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isAvailable}
                      onChange={(e) => setIsAvailable(e.target.checked)}
                    />
                  }
                  label={isAvailable ? "Available" : "Excluded"}
                  sx={{ "& .MuiFormControlLabel-label": { color: "#fff" } }}
                />
                <div style={hintStyle}>
                  {isAvailable
                    ? "This machine counts towards availability and the KPI calculations."
                    : "This machine is left out of availability and the KPI calculations."}
                </div>
              </div>

              <Autocomplete
                options={users}
                value={editor}
                onChange={(_, selected) => setEditor(selected)}
                getOptionLabel={(option) => option.FullName ?? ""}
                isOptionEqualToValue={(option, selected) => option.Id === selected.Id}
                autoHighlight
                openOnFocus
                slotProps={autocompleteSlotProps}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Who is making this change?"
                    placeholder="Start typing a name"
                    InputLabelProps={{ shrink: true }}
                    sx={fieldStyle}
                  />
                )}
              />
            </div>
          ))}
      </DialogContent>

      <DialogActions style={surface}>
        {savedMachine ? (
          <Button
            variant="contained"
            color="success"
            sx={{ color: "#fff" }}
            onClick={close}
          >
            Close
          </Button>
        ) : (
          <>
            <Button
              variant="contained"
              color="error"
              sx={{ color: "#fff" }}
              onClick={close}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              sx={{ color: "#fff" }}
              onClick={save}
              disabled={isSaving || isLoading}
              autoFocus
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MachineEditorDialog;
